import React from 'react';
import { Feather } from '@expo/vector-icons';
import { TouchableOpacity, View } from 'react-native';
import { observer } from 'mobx-react';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useMutation, useQuery } from '@apollo/client';

import Error from '../../../../components/Error/Error';
import Loading from '../../../../components/Loading/Loading';
import Show from '../../../../components/Show/Show';
import { ActivityEntryBody } from '../../../../components/Activity/ActivityEntryBody';
import { ActivityPageTemplate } from '../../../../components/Activity/ActivityPageTemplate';
import { ActivityType } from '../../../../components/ActivityInfo/ActivityInfo';
import { Body1Bold, Body1Reg, Body2Reg, Caption1Bold } from '../../../../config/designSystem/typography';
import { COLORS } from '../../../../config/designSystem/Colors';
import { CREATE_USER_ACTIVITY, SET_ACTIVITY_REMINDER, UPDATE_USER_ACTIVITY } from '../../../../state/mutations/UserActivity';
import { ChallengeCalendar } from '../../../../components/Challenges/ChallengeCalendar';
import { ChallengeSettingsSheet } from '../../../../components/Challenges/ChallengeSettingsSheet';
import { GET_MY_ACTIVITY_ENTRIES } from '../../../../state/queries/Activity';
import { ProgressStreak } from '../../../../components/presenters/ProgressStreak/ProgressStreak';
import { SPACING } from '../../../../config/designSystem/Spacing';
import { SegmentedTabs } from '../../../../components/Challenges/SegmentedTabs';
import { TouchableText } from '../../../../components/TouchableText/TouchableText';
import { branchingKey, privateKey, sessionStackKey } from '../../../../constants/routeKeys';
import { concatPath } from '../../../../utils/links/concatPath';
import { entryPreview } from '../../../../utils/activities/entryBlocks';
import { goTo } from '../../../../utils/links/goTo';
import { logAmplitude } from '../../../../utils/analytics/analytics';
import { trackChallengeAccepted, trackChallengeReminderSet, trackChallengeStopped } from '../../../../utils/analytics/challengeEvents';
import { useFetchedContent } from '../../../../hooks/content/useFetchedContent';
import { useIsConnected } from '../../../../hooks/useIsConnected';
import { useStores } from '../../../../hooks/useStores';

const activityTypeToLabel = (type?: string): ActivityType => {
  switch (type) {
  case 'audio':
    return ActivityType.MEDITATION;
  case 'memo':
    return ActivityType.TOOLING;
  case 'journaling':
  default:
    return ActivityType.JOURNALING;
  }
};

const ActivityOverview = observer(() => {
  // local, not global: every content screen reads an "id" param, and
  // useGlobalSearchParams hands a still-mounted background screen the id of
  // whatever route is focused - the activity page then queried activity(id)
  // with a video/exercise id and logged "Cannot return null for Query.activity"
  const params = useLocalSearchParams();
  const id = params.id;

  const rootStore = useStores();
  const connected = useIsConnected();

  const { loading, error } = useFetchedContent(id as string, 'ACTIVITY');
  // previous entries show a short preview; tapping a card reveals the whole entry
  const [expandedEntry, setExpandedEntry] = React.useState<number | null>(null);
  const [activeTab, setActiveTab] = React.useState<'summary' | 'calendar'>('summary');
  const [settingsVisible, setSettingsVisible] = React.useState(false);

  // the whole history on this activity (challenge days + free entries),
  // refreshed each time the page is shown again (after a session). The first
  // focus is the mount, where the query has just been sent: refetching there
  // (and fetching the user activities from a mount effect too) sent every
  // request twice
  const { data: entriesData, refetch: refetchEntries } = useQuery(GET_MY_ACTIVITY_ENTRIES, {
    variables: { activityId: id },
    skip: !id,
    fetchPolicy: 'network-only',
  });
  const focusedOnce = React.useRef(false);
  useFocusEffect(React.useCallback(() => {
    rootStore?.activity?.fetchMyActivity();
    if (id && focusedOnce.current) {
      refetchEntries();
    }
    focusedOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]));
  const entries: { userActivity: string; isChallenge: boolean; day: number | null; entryIndex: number | null; date: string; entry: string | null }[] =
    entriesData?.myActivityEntries || [];

  const activity = rootStore?.activity?.getActivity(id as string);

  const labels = rootStore?.language?.translate?.MOBILE?.CHALLENGE;
  const stepActionLabels = rootStore?.language?.translate?.MOBILE?.STEP_ACTION;

  const [createUserActivity] = useMutation(CREATE_USER_ACTIVITY);
  const [updateUserActivity] = useMutation(UPDATE_USER_ACTIVITY);
  const [setActivityReminder] = useMutation(SET_ACTIVITY_REMINDER);

  // discovered outside a path session (library, deep link): the challenge can
  // be added right from the overview — day 1 completes on the first play
  const onPressAddToProgram = async () => {
    if (!activity?._id) {
      return;
    }
    try {
      const { data } = await createUserActivity({
        variables: {
          input: {
            activity: activity._id,
            isChallenge: true,
            language: activity.language,
          },
        },
      });
      const userActivityId = data?.createUserActivity?._id;
      if (userActivityId) {
        rootStore.activity.setUserActivity(
          activity._id,
          userActivityId,
          'started',
          true,
          data?.createUserActivity?.days,
        );
        trackChallengeAccepted(activity, 'overview');
      }
    } catch (error) {
      console.error('Could not add the challenge to the program', error);
    }
  };

  const goToHome = () => {
    goTo.dashboard();
  };

  // deterministic exit: tab switches don't always leave usable history
  // entries on web, so a blind router.back() can surface an unrelated tab
  const onPressClose = () => {
    goToHome();
  };

  // "18:00" (stored) <-> "6:00 PM" (picker)
  const to12h = (time?: string | null): string => {
    if (!time) {
      return '6:00 PM';
    }
    const [hoursRaw, minutes] = time.split(':');
    const hours = parseInt(hoursRaw, 10);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const displayed = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayed}:${minutes} ${suffix}`;
  };
  const to24h = (label: string): string => {
    const match = label.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) {
      return label;
    }
    let hours = parseInt(match[1], 10) % 12;
    if (match[3].toUpperCase() === 'PM') {
      hours += 12;
    }
    return `${String(hours).padStart(2, '0')}:${match[2]}`;
  };

  const onSaveReminder = async (enabled: boolean, timeLabel: string) => {
    if (!activity?.userActivityId) {
      return;
    }
    try {
      await setActivityReminder({
        variables: {
          userActivityId: activity.userActivityId,
          reminderEnabled: enabled,
          reminderTime: enabled ? to24h(timeLabel) : null,
        },
      });
      // MST nodes are protected: mutate through the store action
      rootStore.activity.setReminder(activity._id, enabled, enabled ? to24h(timeLabel) : null);
      trackChallengeReminderSet(activity, enabled, enabled ? to24h(timeLabel) : null, 'overview');
    } catch (error) {
      console.error('Could not save the reminder', error);
    }
  };

  const onStopChallenge = async () => {
    if (!activity?.userActivityId) {
      return;
    }
    try {
      const daysBefore = activity.days?.slice() || [];
      await updateUserActivity({
        variables: { input: { _id: activity.userActivityId, status: 'stopped' } },
      });
      rootStore.activity.setUserActivity(activity._id, activity.userActivityId, 'stopped', false, []);
      trackChallengeStopped(activity, 'overview', daysBefore);
      await rootStore.activity.fetchMyActivity();
    } catch (error) {
      console.error('Could not stop the challenge', error);
    }
  };

  const onPressStart = () => {
    if (!activity?.sessionId) {
      return;
    }
    rootStore.activity.setEntryToEdit(null);
    // each day starts with a blank diary: drop the previous day's answers
    rootStore?.responses?.clearSessionResponses(activity.sessionId);
    logAmplitude('Activity Started', {
      activityId: activity._id,
      activityType: activity.type,
    });
    router.push({
      pathname: concatPath([privateKey, sessionStackKey, branchingKey], -1) as string,
      params: {
        sessionId: activity.sessionId,
        source: 'SESSION',
      },
    });
  };

  // rewrite a past entry: the diary opens prefilled and saves back to it
  const onPressUpdateEntry = (entry: { userActivity: string; day: number | null; entryIndex: number | null; entry: string | null }) => {
    if (!activity?.sessionId) {
      return;
    }
    rootStore.activity.setEntryToEdit({
      activityId: activity._id,
      userActivityId: entry.userActivity,
      day: entry.day,
      entryIndex: entry.entryIndex,
      entry: entry.entry || '',
    });
    rootStore?.responses?.clearSessionResponses(activity.sessionId);
    router.push({
      pathname: concatPath([privateKey, sessionStackKey, branchingKey], -1) as string,
      params: { sessionId: activity.sessionId, source: 'SESSION' },
    });
  };

  if (error) {
    return <Error network={!connected} labels={rootStore?.language.translate.MOBILE?.ERROR} />;
  }

  if (loading || !activity) {
    return <Loading withLabel={true} label={stepActionLabels?.LOADING ? stepActionLabels?.LOADING : 'Loading data..'} />;
  }

  const isChallengeInProgress = activity.isChallenge && activity.status === 'started' && activity.days.length > 0;
  const isChallengeCompleted = activity.status === 'completed';

  // today's challenge day is done: nothing more to do today (an entry is
  // rewritten from its own Update link in the history); memos can always be
  // re-read
  const doneToday = rootStore?.activity?.isDoneToday(activity._id);
  const canStart = activity.type === 'memo' || !doneToday;

  // no session to run means no CTA at all — a button that silently does
  // nothing is worse than none (broken activity->session references exist).
  // Done for today: the button stays, greyed out, so the user knows why
  // there is nothing to do (entries are rewritten from their own link)
  const ctaDisabled = !!activity.sessionId && !canStart;
  const ctaLabel = !activity.sessionId
    ? undefined
    : ctaDisabled
      ? (labels?.DONE_TODAY || 'Already completed today ✓')
      : activity.type === 'audio'
        ? (labels?.START_AUDIO || 'Start meditating 🧘')
        : activity.type === 'memo'
          ? (labels?.SEE_MEMO || 'See memo 📖')
          : (labels?.START_JOURNALING || 'Fill out my diary');

  return (
    <ActivityPageTemplate
      designSystemColor={activity.designSystemColor || 'LAVENDER'}
      image={activity.image || null}
      heroType={activity.type}
      onPressSettings={isChallengeInProgress ? () => setSettingsVisible(true) : undefined}
      title={activity?.title}
      activityType={activityTypeToLabel(activity.type)}
      onClose={onPressClose}
      ctaLabel={ctaLabel}
      onPressCta={canStart ? onPressStart : undefined}
      ctaDisabled={ctaDisabled}
    >
      <ChallengeSettingsSheet
        visible={settingsVisible}
        onClose={() => setSettingsVisible(false)}
        reminderEnabled={!!activity.reminderEnabled}
        reminderTime={to12h(activity.reminderTime)}
        onSaveReminder={onSaveReminder}
        onStopChallenge={onStopChallenge}
        labels={labels}
      />
      <Show when={!!activity.isChallenge && activity.status === 'started'}>
        <Caption1Bold color={COLORS.state.success.primary}>
          ✓ {labels?.ADDED_TO_PROGRAM || 'Added to my program'}
        </Caption1Bold>
      </Show>
      <Show when={!!isChallengeCompleted}>
        <Caption1Bold color={COLORS.state.success.primary}>
          {labels?.ALL_DONE || 'Challenge completed 🎉'}
        </Caption1Bold>
      </Show>
      {/* like the CTA below, adding requires a runnable session — a challenge
          whose days can never be completed would just rot in the Today list.
          After a completed challenge the user can start a new week. */}
      <Show when={!!activity.challengeable && !!activity.sessionId && activity.status !== 'started'}>
        <View style={{ alignSelf: 'flex-start' }}>
          <TouchableText
            onPress={onPressAddToProgram}
            label={'📅 ' + (isChallengeCompleted
              ? (labels?.START_NEW_CHALLENGE || 'Start a new challenge')
              : (labels?.ADD_TO_MY_PROGRAM || 'Add to my program'))}
          />
        </View>
      </Show>

      <Body1Reg color={COLORS.content.secondary}>
        {activity?.shortDescription}
      </Body1Reg>

      <Show when={!!isChallengeInProgress}>
        <SegmentedTabs
          tabs={[
            { key: 'summary', label: labels?.SUMMARY_TAB || 'Summary' },
            { key: 'calendar', label: labels?.CALENDAR_TAB || 'Calendar' },
          ]}
          active={activeTab}
          onChange={key => setActiveTab(key as 'summary' | 'calendar')}
        />
      </Show>

      <Show when={!!isChallengeInProgress && activeTab === 'calendar'}>
        <ChallengeCalendar
          days={activity.days.slice()}
          onPressCompleteToday={canStart || activity.type === 'memo' ? onPressStart : undefined}
          showEntry={activity.type !== 'memo'}
          labels={labels}
        />
      </Show>

      <Show when={!!isChallengeInProgress && activeTab === 'summary'}>
        <ProgressStreak
          days={activity.days.slice()}
          title={labels?.IN_PROGRESS || 'Challenge in progress'}
          encouragement={labels?.KEEP_IT_UP}
        />
      </Show>

      {/* memo entries are always the same deck: no history boxes, the
          progression and calendar say it all. Otherwise every run is listed:
          challenge days ("Day n") and free entries written whenever */}
      <Show when={activity.type !== 'memo' && (!isChallengeInProgress || activeTab === 'summary') && entries.some(entry => !!entry.entry)}>
        <View style={{ rowGap: SPACING['sp-12'] }}>
          <Body1Bold>{labels?.PREVIOUS_ENTRIES || 'My previous entries'}</Body1Bold>
          {entries
            .map((entry, index) => ({ entry, index }))
            .filter(({ entry }) => !!entry.entry)
            .map(({ entry, index }) => {
              const expanded = expandedEntry === index;
              const preview = entryPreview(entry.entry as string);
              const kind = entry.isChallenge
                ? (labels?.DAY || 'Day') + ' ' + entry.day
                : (labels?.FREE_ENTRY || 'Free entry');
              // any diary entry can be rewritten from here
              const canUpdate = expanded && activity.type === 'journaling' && !!activity.sessionId;
              return (
                <TouchableOpacity
                  key={entry.userActivity + '-' + index}
                  activeOpacity={0.7}
                  onPress={() => setExpandedEntry(expanded ? null : index)}
                  style={{
                    backgroundColor: COLORS.surface.contrast,
                    borderRadius: SPACING['sp-12'],
                    borderWidth: 1,
                    borderColor: COLORS.border.secondary,
                    padding: SPACING['sp-16'],
                    rowGap: SPACING['sp-8'],
                  }}
                >
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Body1Bold>
                      {kind + ' — ' + new Date(entry.date).toLocaleDateString()}
                    </Body1Bold>
                    <Feather
                      name={expanded ? 'chevron-up' : 'chevron-down'}
                      size={18}
                      color={COLORS.content.secondary}
                    />
                  </View>
                  <Show when={!expanded}>
                    <Body2Reg selectable={true} color={COLORS.content.secondary}>
                      {preview}
                    </Body2Reg>
                  </Show>
                  <Show when={expanded}>
                    <ActivityEntryBody entry={entry.entry as string} />
                  </Show>
                  <Show when={canUpdate}>
                    <View style={{ alignSelf: 'flex-start' }}>
                      <TouchableText
                        onPress={() => onPressUpdateEntry(entry)}
                        label={(labels?.UPDATE_ENTRY || 'Update') + ' ✏️'}
                      />
                    </View>
                  </Show>
                </TouchableOpacity>
              );
            })}
        </View>
      </Show>
    </ActivityPageTemplate>
  );
});

export default ActivityOverview;
